import React from 'react'

export default function PurchaseSuccessModal({
  course,
  selectedPackage,
  bookingMode,
  welcomeMessage,
  onClose,
  onBookNow,
  colors,
  translations: t = {}
}) {
  const isGroupClass = selectedPackage?.isGroupClass
  const enrolledFromRegistered = selectedPackage?.enrolledFromRegistered
  const isGift = selectedPackage?.isGift

  const modalTitle = isGift
    ? (t.giftTitle || 'Gift Sent!')
    : enrolledFromRegistered
      ? (t.enrolledTitle || 'Enrollment Successful!')
      : (t.title || 'Purchase Successful!')

  const description = isGift
    ? (t.giftDescription || 'You have gifted %{count} lesson(s) of "%{course_name}" to %{recipient}.')
        .replace('%{count}', selectedPackage.lessonCount)
        .replace('%{course_name}', course.name)
        .replace('%{recipient}', selectedPackage.recipientName || '')
    : isGroupClass
      ? (t.groupClassDescription || 'You have successfully enrolled in "%{course_name}" (%{count} sessions).')
          .replace('%{count}', selectedPackage.lessonCount)
          .replace('%{course_name}', course.name)
      : (t.description || 'You have purchased %{count} lesson(s) of "%{course_name}".')
          .replace('%{count}', selectedPackage.lessonCount)
          .replace('%{course_name}', course.name)

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
        {/* Success Header */}
        <div
          className="px-6 py-8 text-center"
          style={{ backgroundColor: colors.background }}
        >
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: '#10B981' }}
          >
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {modalTitle}
          </h2>
          <p className="text-gray-600">
            {description}
          </p>
        </div>

        {/* Content - Welcome message + booking mode actions */}
        <div className="p-6">
          {/* Welcome Message */}
          {welcomeMessage && (
            <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-lg p-4 mb-4">
              <p className="text-sm font-medium text-amber-800 mb-1">{t.messageFromTeam || 'Message from the team'}</p>
              <p className="text-sm text-amber-700 whitespace-pre-line">{welcomeMessage}</p>
            </div>
          )}
          {isGift ? (
            // Gift purchase: simple confirmation, no booking CTA
            <div className="text-center">
              <div className="bg-green-50 rounded-xl p-4 mb-4">
                <p className="text-sm text-green-700">
                  {t.giftConfirmed || 'The lessons have been added to their account. They will also receive an email notification.'}
                </p>
              </div>
              <button
                onClick={onClose}
                className="w-full py-3 text-white rounded-xl font-semibold transition-colors cursor-pointer"
                style={{ backgroundColor: colors.primary }}
              >
                {t.ok || 'OK'}
              </button>
            </div>
          ) : isGroupClass ? (
            // Group class: show schedule link
            <div className="text-center">
              <div className="bg-green-50 rounded-xl p-4 mb-4">
                <p className="text-sm text-green-700">
                  {t.groupClassConfirmed || 'Your class schedule is set. You\'ll receive a reminder before each session.'}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 py-3 text-gray-600 hover:bg-gray-100 rounded-xl font-medium transition-colors border border-gray-200 cursor-pointer"
                >
                  {t.ok || 'OK'}
                </button>
                <a
                  // eslint-disable-next-line no-restricted-syntax -- the backstage has no locale routes, so a prefix here is a 404
                  href="/student/dashboard"
                  className="flex-1 py-3 text-white rounded-xl font-semibold transition-colors text-center"
                  style={{ backgroundColor: colors.primary }}
                >
                  {t.viewSchedule || 'View Schedule'}
                </a>
              </div>
            </div>
          ) : bookingMode === 'self_booking' ? (
            // Self-booking mode: Show booking options
            <div className="text-center">
              <div className="bg-blue-50 rounded-xl p-4 mb-4">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="font-medium text-blue-800">{t.bookALessonNow || 'Book a Lesson Now'}</span>
                </div>
                <p className="text-sm text-blue-600">
                  {t.bookALessonDescription || 'You can select a convenient time slot to schedule your lesson right away.'}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 py-3 text-gray-600 hover:bg-gray-100 rounded-xl font-medium transition-colors border border-gray-200 cursor-pointer"
                >
                  {t.bookLater || 'Book Later'}
                </button>
                <button
                  onClick={onBookNow || onClose}
                  className="flex-1 py-3 text-white rounded-xl font-semibold transition-colors cursor-pointer"
                  style={{ backgroundColor: colors.primary }}
                >
                  {t.bookNow || 'Book Now'}
                </button>
              </div>
            </div>
          ) : (
            // Teacher-managed mode: Show waiting message
            <div className="text-center">
              <div className="bg-amber-50 rounded-xl p-4 mb-4">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="font-medium text-amber-800">{t.waitingForTeacher || 'Waiting for Teacher to Schedule'}</span>
                </div>
                <p
                  className="text-sm text-amber-700"
                  dangerouslySetInnerHTML={{ __html: t.waitingDescriptionHtml || 'Your teacher will contact you soon to arrange a lesson time.<br />Please wait for a notification!' }}
                />
              </div>
              <p className="text-xs text-gray-400 mb-4">
                {t.replyNotice || 'You will typically receive a reply within 1–2 business days.'}
              </p>
              <button
                onClick={onClose}
                className="w-full py-3 text-white rounded-xl font-semibold transition-colors cursor-pointer"
                style={{ backgroundColor: colors.primary }}
              >
                {t.viewMyCourses || 'View My Courses'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
